import React from 'react';
import VeoModelSelector from './VeoModelSelector';
import VeoAudioControls from './VeoAudioControls';
import VeoAdvancedControls from './VeoAdvancedControls';
import VeoStylePresets from './VeoStylePresets';
import { getVeoModel } from '../../../services/veoService';

interface VeoVideoControlsProps {
  // Model selection
  veoModel: string;
  onVeoModelChange: (model: string) => void;
  
  // Audio controls
  dialogueCue: string;
  onDialogueCueChange: (value: string) => void;
  soundEffects: string;
  onSoundEffectsChange: (value: string) => void;
  ambientNoise: string;
  onAmbientNoiseChange: (value: string) => void;
  audioStyle: string;
  onAudioStyleChange: (value: string) => void;
  
  // Advanced visual controls
  visualStyle: string;
  onVisualStyleChange: (value: string) => void;
  lighting: string;
  onLightingChange: (value: string) => void;
  cameraMovement: string;
  onCameraMovementChange: (value: string) => void;
  motionIntensity: string;
  onMotionIntensityChange: (value: string) => void;
  mood: string;
  onMoodChange: (value: string) => void;
  
  // Safety controls
  personGeneration: string;
  onPersonGenerationChange: (value: string) => void;
  negativePrompt: string;
  onNegativePromptChange: (value: string) => void;
  
  // Accordion states
  showVeoAudioAccordion: boolean;
  onToggleVeoAudioAccordion: () => void;
  showVeoVisualAccordion: boolean;
  onToggleVeoVisualAccordion: () => void;
  showVeoMotionAccordion: boolean;
  onToggleVeoMotionAccordion: () => void;
  showVeoSafetyAccordion: boolean;
  onToggleVeoSafetyAccordion: () => void;
}

const VeoVideoControls: React.FC<VeoVideoControlsProps> = ({
  veoModel,
  onVeoModelChange,
  dialogueCue,
  onDialogueCueChange,
  soundEffects,
  onSoundEffectsChange,
  ambientNoise,
  onAmbientNoiseChange,
  audioStyle,
  onAudioStyleChange,
  visualStyle,
  onVisualStyleChange,
  lighting,
  onLightingChange,
  cameraMovement,
  onCameraMovementChange,
  motionIntensity,
  onMotionIntensityChange,
  mood,
  onMoodChange,
  personGeneration,
  onPersonGenerationChange,
  negativePrompt,
  onNegativePromptChange,
  showVeoAudioAccordion,
  onToggleVeoAudioAccordion,
  showVeoVisualAccordion,
  onToggleVeoVisualAccordion,
  showVeoMotionAccordion,
  onToggleVeoMotionAccordion,
  showVeoSafetyAccordion,
  onToggleVeoSafetyAccordion
}) => {
  const selectedModelData = getVeoModel(veoModel);
  const isAudioEnabled = selectedModelData?.hasAudio || false;

  const handlePresetSelect = (preset: any) => {
    // Apply preset values to state
    onVisualStyleChange(preset.visual_style || '');
    onLightingChange(preset.lighting || '');
    onCameraMovementChange(preset.camera_movement || '');
    onAudioStyleChange(preset.audio_style || '');
  };

  return (
    <div style={{ marginBottom: '20px' }}>
      {/* Model Selection */}
      <VeoModelSelector
        selectedModel={veoModel}
        onModelChange={onVeoModelChange}
      />

      {/* Style Presets */}
      <VeoStylePresets
        onPresetSelect={handlePresetSelect}
      />

      {/* Audio Controls Accordion */}
      <div style={{ marginBottom: '16px' }}>
        <div 
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '12px 16px',
            backgroundColor: isAudioEnabled 
              ? 'rgba(16, 185, 129, 0.1)' 
              : 'rgba(107, 114, 128, 0.1)',
            borderRadius: '6px',
            border: isAudioEnabled 
              ? '1px solid rgba(16, 185, 129, 0.3)' 
              : '1px solid rgba(107, 114, 128, 0.3)',
            cursor: 'pointer',
            marginBottom: '4px',
            transition: 'all 0.2s ease'
          }}
          onClick={onToggleVeoAudioAccordion}
        >
          <span style={{ 
            fontSize: '14px', 
            fontWeight: '500', 
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            🎵 Audio Generation
            {isAudioEnabled && (
              <span style={{
                padding: '2px 6px',
                backgroundColor: '#10b981',
                color: '#fff',
                fontSize: '10px',
                borderRadius: '8px',
                fontWeight: '600'
              }}>
                ENABLED
              </span>
            )}
          </span>
          <span style={{ 
            color: isAudioEnabled ? '#10b981' : '#8b5cf6', 
            fontSize: '18px' 
          }}>
            {showVeoAudioAccordion ? '−' : '+'}
          </span>
        </div>
        
        {showVeoAudioAccordion && (
          <div style={{
            padding: '16px',
            backgroundColor: '#1a1a1a',
            borderRadius: '6px',
            border: '1px solid rgba(139, 92, 246, 0.2)',
            marginBottom: '16px'
          }}>
            <VeoAudioControls
              dialogueCue={dialogueCue}
              onDialogueCueChange={onDialogueCueChange}
              soundEffects={soundEffects}
              onSoundEffectsChange={onSoundEffectsChange}
              ambientNoise={ambientNoise}
              onAmbientNoiseChange={onAmbientNoiseChange}
              audioStyle={audioStyle}
              onAudioStyleChange={onAudioStyleChange}
              isAudioEnabled={isAudioEnabled}
            />
          </div>
        )}
      </div>

      {/* Advanced Controls */}
      <VeoAdvancedControls
        visualStyle={visualStyle}
        onVisualStyleChange={onVisualStyleChange}
        lighting={lighting}
        onLightingChange={onLightingChange}
        cameraMovement={cameraMovement}
        onCameraMovementChange={onCameraMovementChange}
        motionIntensity={motionIntensity}
        onMotionIntensityChange={onMotionIntensityChange}
        mood={mood}
        onMoodChange={onMoodChange}
        personGeneration={personGeneration}
        onPersonGenerationChange={onPersonGenerationChange}
        negativePrompt={negativePrompt}
        onNegativePromptChange={onNegativePromptChange}
        showVisualAccordion={showVeoVisualAccordion}
        onToggleVisualAccordion={onToggleVeoVisualAccordion}
        showMotionAccordion={showVeoMotionAccordion}
        onToggleMotionAccordion={onToggleVeoMotionAccordion}
        showSafetyAccordion={showVeoSafetyAccordion}
        onToggleSafetyAccordion={onToggleVeoSafetyAccordion}
      />

      {/* Cost Information
      {selectedModelData && (
        <div style={{
          padding: '12px',
          backgroundColor: 'rgba(139, 92, 246, 0.05)',
          borderRadius: '6px',
          border: '1px solid rgba(139, 92, 246, 0.2)',
          marginTop: '16px'
        }}>
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <span style={{ 
              fontSize: '13px', 
              color: '#ccc'
            }}>
              💰 Generation Cost:
            </span>
            <span style={{ 
              fontSize: '14px', 
              fontWeight: 'bold',
              color: '#10b981'
            }}>
              ${selectedModelData.cost.toFixed(2)} per video
            </span>
          </div>
          <div style={{ 
            fontSize: '11px', 
            color: '#8b5cf6',
            marginTop: '4px'
          }}>
            {selectedModelData.duration}s • 720p HD{isAudioEnabled ? ' • Native Audio' : ' • Silent'}
          </div>
        </div>
      )} */}
    </div>
  );
};

export default VeoVideoControls;